import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { GenericDialogLayout } from '../GenericDialogLayout'
import { FormTextField, FormSelectField, AppForm } from '../FormCore'
import { MainButton } from '../MainButton'
import { ShowDialogProps } from 'src/show-dialog'
import { useGetFormSchemasDropdown } from 'src/api/form-schema'
import { ReportTemplateLocation } from 'src/enums'
import { IDropdownDto } from 'src/generated-types/api-result'
import { ReportDesignerContainer } from 'src/screens/customer/report-generator/ReportDesignerContainer'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { useZodForm } from 'src/hooks/zod-form'
import { SchemaType } from 'src/generated-types/form-schema'

interface ILocationOption {
  id: ReportTemplateLocation
  name: string
}

interface ICreateReportTemplateForm {
  templateName: string
  locations: ILocationOption[]
  formSchema: IDropdownDto | null
}

export interface ICreateReportTemplateDialogProps extends ShowDialogProps {
  customerId: number
  formSchemaId?: number
}

export const CreateReportTemplateDialog: React.FC<ICreateReportTemplateDialogProps> = ({
  customerId,
  formSchemaId,
  pop,
}) => {
  const { t: translate } = useTranslation(['dynamic-report', 'common'])
  const { data: formSchemas = [], isLoading } = useGetFormSchemasDropdown(customerId, SchemaType.FormSchema)

  const { showDialog } = useDialogCtx()

  const locationOptionSchema: z.ZodType<ILocationOption> = z.object({
    id: z.nativeEnum(ReportTemplateLocation),
    name: z.string(),
  })

  const createReportTemplateSchema: z.ZodType<ICreateReportTemplateForm> = z.object({
    templateName: z.string().min(1, { message: translate('template-name-required', { ns: 'dynamic-report' }) }),
    locations: z.array(locationOptionSchema),
    formSchema: z
      .object({ id: z.number(), name: z.string() })
      .nullable()
      .refine(val => val !== null, { message: translate('select-form-schema', { ns: 'dynamic-report' }) }),
  })

  //The location filter is hidden for this version. Will be available once the locations are clarified
  // const locationOptions: ILocationOption[] = Object.keys(ReportTemplateLocation)
  //   .filter(key => isNaN(Number(key)))
  //   .map(key => ({
  //     id: ReportTemplateLocation[key as keyof typeof ReportTemplateLocation],
  //     name: translate(key, { ns: 'dynamic-report' }),
  //   }))

  const form = useZodForm(createReportTemplateSchema, {
    defaultValues: {
      templateName: '',
      locations: [],
      formSchema: null,
    },
  })

  useEffect(() => {
    if (formSchemaId && formSchemas.length > 0) {
      const foundSchema = formSchemas.find(fs => fs.id === formSchemaId) || null
      form.setValue('formSchema', foundSchema)
    }
  }, [formSchemaId, formSchemas])

  const handleNext = async (data: ICreateReportTemplateForm) => {
    await showDialog(
      ReportDesignerContainer,
      {
        componentProps: {
          customerId: customerId as EntityId,
          templateName: data.templateName,
          locations: data.locations.map(l => l.id),
          formSchema: data.formSchema,
        },
        opts: {
          maxWidth: 'xxl',
          fullWidth: true,
          PaperProps: {
            sx: {
              maxHeight: '90vh',
            },
          },
        },
      },
      undefined,
      true,
    )
  }

  return (
    <GenericDialogLayout
      title={translate('create-report-template', { ns: 'dynamic-report' })}
      pop={pop}
      removedCloseButton={false}
      actions={[
        <MainButton key="next" onClick={form.handleSubmit(handleNext)}>
          {translate('next', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form}>
        <FormTextField
          name="templateName"
          label={translate('template-name', { ns: 'dynamic-report' })}
          placeholder={translate('template-name-placeholder', { ns: 'dynamic-report' })}
          fullWidth
        />
        {/* The location filter is hidden for this version. Will be available once the locations are clarified */}
        {/* <FormSelectField<ILocationOption>
          name="locations"
          data={locationOptions}
          label={translate('locations', { ns: 'dynamic-report' })}
          multiple
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        /> */}
        <FormSelectField<IDropdownDto>
          name="formSchema"
          data={formSchemas}
          label={translate('form-schema', { ns: 'dynamic-report' })}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={isLoading}
          placeholder={translate('choose-form-schema', { ns: 'dynamic-report' })}
          disabled={formSchemaId !== undefined}
        />
      </AppForm>
    </GenericDialogLayout>
  )
}
